import React from 'react';
import { formatSigned } from '../../utils/bidi';

/**
 * CO-933 — signed numbers inside RTL copy.
 *
 * A leading "+" or "-" is a weak bidi character. When it is the first run of an Arabic paragraph the
 * bidi algorithm resolves it to the paragraph's direction and paints it AFTER the digits ("+12.3"
 * reads "12.3+", "-15%" reads "15%-"). Isolating the number as an LTR run keeps the sign beside the
 * figure in every locale, and changes nothing in en/fr where the paragraph is already LTR. Screen
 * readers read the characters, so the sign is still announced; the isolate marks are not spoken.
 */

export interface SignedValueProps extends Omit<React.HTMLAttributes<HTMLElement>, 'children' | 'dir'> {
  /** A number gets the explicit "+" (see formatSigned); a preformatted string is shown as is. */
  value: number | string;
  /** Decimals for a numeric value. */
  digits?: number;
  /** Text kept inside the isolate after the figure, e.g. "%". */
  suffix?: string;
}

/** A signed figure as an LTR isolate (<bdi dir="ltr">), so the sign stays on the left of the digits in RTL. */
export function SignedValue({ value, digits, suffix = '', className, ...rest }: SignedValueProps) {
  const body = typeof value === 'number' ? formatSigned(value, digits) : value;
  return (
    <bdi dir="ltr" className={className} {...rest}>
      {body}
      {suffix}
    </bdi>
  );
}

/**
 * A signed number at the start of the text or after a space / "(" — "-2 pts", "net +3", "+0%".
 * A hyphen inside a token ("2026-10") is not a sign and is left alone.
 */
const SIGNED_RUN = /(^|[\s(])([+\-−]\d[\d.,]*%?)/g;

/**
 * A preformatted string (KPI change, "-2 pts") with every signed number in it isolated as LTR and
 * the words around it left in the paragraph's direction, so a translated unit still reads in order.
 * The text content is unchanged.
 */
export function SignedText({ text: input }: { text: string | number | null | undefined }) {
  const text = input == null ? '' : String(input);
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(SIGNED_RUN)) {
    const start = (m.index ?? 0) + m[1].length;
    if (start > last) parts.push(text.slice(last, start));
    parts.push(<SignedValue key={start} value={m[2]} />);
    last = start + m[2].length;
  }
  if (parts.length === 0) return <>{text}</>;
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

export default SignedValue;
